import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { defaultRetry, defaultThrowOnError } from "@/queries/errors";
import { autoPaginate } from "@/queries/pagination";
import { keyWithPrincipal } from "@/queries/principal-key";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { toPlainMessage, type PartialMessage, type PlainMessage } from "@bufbuild/protobuf";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    CreateSCIMConfigurationRequest,
    DeleteSCIMConfigurationRequest,
    RegenerateSCIMTokenRequest,
    UpdateSCIMConfigurationRequest,
    type SCIMConfiguration,
} from "gitpod-next-api/gitpod/v1/organization_pb";
import {
    ListAuditLogsRequest,
    ResourceType,
    type ListAuditLogsResponse_AuditLogEntry,
} from "gitpod-next-api/gitpod/v1/event_pb";

export type PlainSCIMConfiguration = PlainMessage<SCIMConfiguration>;

export type PlainAuditLogEntry = PlainMessage<ListAuditLogsResponse_AuditLogEntry>;

export type SCIMOperationsFilter = {
    resourceTypes?: ResourceType[];
};

export const scimQueryKey = {
    list: (organizationId: string) => keyWithPrincipal(["scim-configs", "list", { organizationId }]),
    get: (scimConfigurationId: string) => keyWithPrincipal(["scim-config", "get", { scimConfigurationId }]),
    operationsLog: (organizationId: string, filters: SCIMOperationsFilter) =>
        keyWithPrincipal(["scim-operations-log", { organizationId, filters }]),
};

/**
 * Hook to list all SCIM configurations for the authenticated user's organization
 */
export const useListSCIMConfigurations = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: scimQueryKey.list(user?.organizationId || ""),
        queryFn: async () => {
            if (!user) {
                throw new Error("Not authenticated");
            }

            const scimConfigurations = await autoPaginate(
                (params) => api.organizationService.listSCIMConfigurations(params),
                {},
                (response) => response.scimConfigurations,
            );

            return scimConfigurations.map((config) => toPlainMessage(config));
        },
        staleTime: 60_000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!user?.organizationId,
    });
};

/**
 * Hook to create a new SCIM configuration
 * Returns both the configuration and the bearer token (shown only once!)
 */
export const useCreateSCIMConfiguration = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async ({
            organizationId,
            name,
            ssoConfigurationId,
        }: {
            organizationId: string;
            name?: string;
            ssoConfigurationId: string;
        }) => {
            const response = await api.organizationService.createSCIMConfiguration(
                new CreateSCIMConfigurationRequest({
                    organizationId,
                    name,
                    ssoConfigurationId,
                }),
            );

            if (!response.scimConfiguration || !response.token) {
                throw new Error("Failed to create SCIM configuration");
            }

            return {
                scimConfiguration: toPlainMessage(response.scimConfiguration),
                token: response.token,
            };
        },
        onSuccess: async () => {
            await client.invalidateQueries({
                queryKey: scimQueryKey.list(user?.organizationId || ""),
            });
        },
    });
};

/**
 * Hook to update an existing SCIM configuration
 * Supports updating name, enabled status, and linked SSO configuration
 */
export const useUpdateSCIMConfiguration = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async ({
            scimConfigurationId,
            name,
            enabled,
            ssoConfigurationId,
        }: PartialMessage<UpdateSCIMConfigurationRequest>) => {
            const response = await api.organizationService.updateSCIMConfiguration(
                new UpdateSCIMConfigurationRequest({
                    scimConfigurationId,
                    name,
                    enabled,
                    ssoConfigurationId,
                }),
            );

            if (!response.scimConfiguration) {
                throw new Error("Failed to update SCIM configuration");
            }

            return toPlainMessage(response.scimConfiguration);
        },
        onSuccess: async () => {
            await client.invalidateQueries({
                queryKey: scimQueryKey.list(user?.organizationId || ""),
            });
        },
    });
};

/**
 * Hook to delete a SCIM configuration
 */
export const useDeleteSCIMConfiguration = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async (scimConfigurationId: string) => {
            await api.organizationService.deleteSCIMConfiguration(
                new DeleteSCIMConfigurationRequest({
                    scimConfigurationId,
                }),
            );
        },
        onSuccess: async () => {
            await client.invalidateQueries({
                queryKey: scimQueryKey.list(user?.organizationId || ""),
            });
        },
    });
};

/**
 * Hook to regenerate the SCIM bearer token
 * Returns the new token (shown only once!), invalidates the old token
 * Does NOT invalidate list query since configuration data doesn't change
 */
export const useRegenerateSCIMToken = () => {
    const api = useGitpodAPI();

    return useMutation({
        mutationFn: async (scimConfigurationId: string) => {
            const response = await api.organizationService.regenerateSCIMToken(
                new RegenerateSCIMTokenRequest({
                    scimConfigurationId,
                }),
            );

            if (!response.token) {
                throw new Error("Failed to regenerate SCIM token");
            }

            return { token: response.token };
        },
        // Note: We don't invalidate queries here since the configuration itself doesn't change
        // Only the token is rotated, which is not stored in the configuration
    });
};

/**
 * Hook to list SCIM operations from audit logs
 * Fetches recent operations (max 500) and paginates client-side
 */
export const useSCIMOperationsLog = (filters: SCIMOperationsFilter, currentPage: number, pageSize = 25) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: scimQueryKey.operationsLog(user?.organizationId || "", filters),
        queryFn: async () => {
            if (!user) {
                throw new Error("Not authenticated");
            }

            // Default to User and Group types if not specified
            const subjectTypes = filters.resourceTypes || [ResourceType.USER, ResourceType.GROUP];

            // Fetch recent 500 operations max to limit dataset
            const response = await api.eventService.listAuditLogs(
                new ListAuditLogsRequest({
                    filter: {
                        subjectTypes,
                    },
                    pagination: {
                        pageSize: 500, // Fetch all recent, paginate client-side
                    },
                }),
            );

            const entries = response.entries.map((entry) => toPlainMessage(entry));

            // Client-side pagination from the recent 500
            const startIndex = currentPage * pageSize;
            const endIndex = startIndex + pageSize;
            const paginatedEntries = entries.slice(startIndex, endIndex);

            return {
                entries: paginatedEntries,
                totalCount: entries.length,
                totalPages: Math.ceil(entries.length / pageSize),
            };
        },
        enabled: !!user?.organizationId,
        staleTime: 30_000,
        placeholderData: keepPreviousData,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

// Re-export ResourceType for use in components
export { ResourceType };
